import React, { useEffect, useState } from 'react';
import { cn } from '../../lib/cn';
import { Icon } from '../Icon/Icon';
import { ViewportOverlay } from '../Overlay/ViewportOverlay';
import { SetInput, SetSelect, SetSwitch } from '../SettingsShell/controls';
import type { EscalationRule, NoAnswer, ThreadBehaviour, TransferRule } from '../../settings-channels/types';

/* ───────────────────────── Add a transfer rule (Call) ───────────────────── */

export interface TransferRuleModalProps {
  open: boolean;
  onClose: () => void;
  onAdd: (rule: TransferRule) => void;
}

const NO_ANSWER: { value: NoAnswer; label: string; desc: React.ReactNode }[] = [
  {
    value: 'take_message',
    label: 'Take a message and raise a Triage note',
    desc: (
      <>
        ELVA comes back on the line, takes what the patient wanted and logs it.{' '}
        <strong className="text-[var(--text-secondary)]">Nobody is left listening to a ring tone.</strong>
      </>
    ),
  },
  {
    value: 'voicemail',
    label: 'Send to voicemail',
    desc: 'The practice’s existing voicemail. Fast, and nothing is logged in ELVA.',
  },
  {
    value: 'next_number',
    label: 'Try the next number, then take a message',
    desc: 'For a rule with a backup — an on-call phone, or a second front-desk line.',
  },
];

const DESTINATIONS = [
  { value: '+1 (210) 555–0101', label: 'Front desk — +1 (210) 555–0101' },
  { value: '+1 (210) 555–0102', label: 'Treatment coordinator — +1 (210) 555–0102' },
  { value: '+1 (210) 555–0100', label: 'Main line — +1 (210) 555–0100' },
];

/**
 * Call · Add a transfer rule (spec 15 §9.4). The only one of the two with a
 * destination, because a call is the only channel where a handover happens in
 * real time. Rules are checked in page order; the first that matches wins.
 */
export function TransferRuleModal({ open, onClose, onAdd }: TransferRuleModalProps) {
  const [condition, setCondition] = useState('');
  const [destination, setDestination] = useState(DESTINATIONS[0]?.value ?? '');
  const [sayBefore, setSayBefore] = useState('');
  const [ifNobody, setIfNobody] = useState<NoAnswer>('take_message');

  useEffect(() => {
    if (open) {
      setCondition('');
      setDestination(DESTINATIONS[0]?.value ?? '');
      setSayBefore('');
      setIfNobody('take_message');
    }
  }, [open]);

  const valid = condition.trim() !== '' && destination !== '';

  return (
    <Shell
      open={open}
      onClose={onClose}
      title="Add a transfer rule"
      sub={
        <>
          When a call should stop being ELVA&rsquo;s and become somebody&rsquo;s.{' '}
          <strong className="text-[var(--text-secondary)]">
            Rules are checked in the order they appear on the page
          </strong>{' '}
          — the first that matches wins.
        </>
      }
      footerNote={
        <>
          Applies to <strong className="text-[var(--text-secondary)]">every agent that calls</strong>, not only the
          Receptionist.
        </>
      }
      valid={valid}
      onSubmit={() => {
        onAdd({
          id: `t_${String(Date.now())}`,
          condition: condition.trim(),
          destination,
          ...(sayBefore.trim() ? { sayBefore: sayBefore.trim() } : {}),
          ifNobodyPicksUp: ifNobody,
        });
      }}
    >
      <Field
        label="What the patient is asking about"
        desc={
          <>
            Plain English, the way you would describe it to a new receptionist.{' '}
            <strong className="text-[var(--text-secondary)]">ELVA matches meaning, not words</strong>, so{' '}
            <em>&ldquo;wants to argue about a bill&rdquo;</em> catches <em>&ldquo;why was I charged&rdquo;</em> as well.
          </>
        }
      >
        <SetInput
          ariaLabel="What the patient is asking about"
          value={condition}
          onChange={setCondition}
          placeholder="Billing disputes, payment plans, or any financial question"
        />
      </Field>

      <Field
        label="Transfer to"
        desc={
          <>
            One of this location&rsquo;s numbers, or any number you type.{' '}
            <strong className="text-[var(--text-secondary)]">
              ELVA briefs whoever picks up before connecting them
            </strong>{' '}
            — who is calling, what they already said, and why it was escalated, so the patient never repeats themselves.
          </>
        }
      >
        <SetSelect ariaLabel="Transfer to" value={destination} options={DESTINATIONS} onChange={setDestination} />
      </Field>

      <Field
        label="What ELVA says before transferring"
        desc="Optional. Left blank, ELVA uses its own wording. Fill it in when the handover needs a particular phrase — a name, or a warning that there may be a wait."
      >
        <SetInput
          ariaLabel="What ELVA says before transferring"
          value={sayBefore}
          onChange={setSayBefore}
          placeholder="Let me put you through to Sarah, who handles billing. One moment."
        />
      </Field>

      <Field
        last
        label="If nobody picks up"
        desc={
          <>
            <strong className="text-[var(--text-secondary)]">The case a transfer rule usually forgets.</strong> A
            ringing phone at 4:55pm on a Friday is the most likely moment for this rule to fire and the least likely
            moment for anybody to answer it.
          </>
        }
      >
        <RadioGroup
          name="if-nobody"
          value={ifNobody}
          options={NO_ANSWER}
          onChange={(v) => { setIfNobody(v); }}
        />
      </Field>
    </Shell>
  );
}

/* ─────────────────── Add an escalation rule (SMS / Email) ───────────────── */

export interface EscalationRuleModalProps {
  open: boolean;
  /** On email there is no live thread to keep replying to, so the choice is not offered. */
  channel: 'sms' | 'email';
  onClose: () => void;
  onAdd: (rule: EscalationRule) => void;
}

const THREAD: { value: ThreadBehaviour; label: string; desc: React.ReactNode }[] = [
  {
    value: 'keep_replying',
    label: 'Keep replying, and raise a note',
    desc: (
      <>
        <strong className="text-[var(--text-secondary)]">The default, and right almost always.</strong> ELVA carries on
        answering what it can while the note waits. The patient never gets a dead thread.
      </>
    ),
  },
  {
    value: 'stop_replying',
    label: 'Stop replying, and raise a note',
    desc: (
      <>
        ELVA goes quiet on this thread until a person picks it up. For topics where{' '}
        <strong className="text-[var(--text-secondary)]">any</strong> further automated reply would be the wrong thing —
        a legal letter, a bereavement.
      </>
    ),
  },
];

/** SMS and Email · Add an escalation rule. One modal for both (§9.4). */
export function EscalationRuleModal({ open, channel, onClose, onAdd }: EscalationRuleModalProps) {
  const [condition, setCondition] = useState('');
  const [thread, setThread] = useState<ThreadBehaviour>('keep_replying');
  const [urgent, setUrgent] = useState(false);

  useEffect(() => {
    if (open) {
      setCondition('');
      // On email there is no live thread, so it is fixed to raise a note.
      setThread(channel === 'email' ? 'stop_replying' : 'keep_replying');
      setUrgent(false);
    }
  }, [open, channel]);

  const valid = condition.trim() !== '';

  return (
    <Shell
      open={open}
      onClose={onClose}
      title="Add an escalation rule"
      sub={
        <>
          When a message should reach a person.{' '}
          <strong className="text-[var(--text-secondary)]">
            Nothing is transferred and nothing is promised to the patient
          </strong>{' '}
          — a Triage note is raised and somebody picks it up.
        </>
      }
      footerNote={
        <>
          Applies to <strong className="text-[var(--text-secondary)]">every agent on this channel</strong>, not only the
          Receptionist.
        </>
      }
      valid={valid}
      onSubmit={() => {
        onAdd({ id: `e_${String(Date.now())}`, condition: condition.trim(), threadBehaviour: thread, urgent });
      }}
    >
      <Field
        label="What the patient is writing about"
        desc={
          <>
            Plain English. <strong className="text-[var(--text-secondary)]">ELVA matches meaning, not words</strong> —{' '}
            <em>&ldquo;unhappy about how they were treated&rdquo;</em> catches a complaint however it is phrased.
          </>
        }
      >
        <SetInput
          ariaLabel="What the patient is writing about"
          value={condition}
          onChange={setCondition}
          placeholder="Complaints about earlier care, or about a member of staff"
        />
      </Field>

      {channel === 'sms' ? (
        <Field label="What ELVA does with the thread">
          <RadioGroup name="thread" value={thread} options={THREAD} onChange={(v) => { setThread(v); }} />
        </Field>
      ) : (
        <Field
          label="What ELVA does with the thread"
          desc="On email there is no live thread to keep replying to, so this is fixed to raise a note."
        >
          <div className="rounded-[10px] border border-[var(--border-default)] bg-[var(--surface-sunken)] px-3.5 py-3 text-[12.5px] text-[var(--text-secondary)]">
            Raise a note. ELVA does not reply again on this thread until a person picks it up.
          </div>
        </Field>
      )}

      <Field
        label="Mark the note urgent"
        desc={
          <>
            It sorts to the top of the Triage queue.{' '}
            <strong className="text-[var(--text-secondary)]">Use it sparingly</strong> — a queue where a third of the
            notes are urgent has no urgent notes at all.
          </>
        }
      >
        <span className="flex items-center gap-3">
          <SetSwitch label="Mark the note urgent" checked={urgent} onChange={setUrgent} />
          <span className="text-[12px] text-[var(--text-muted)]">
            {urgent ? 'On — sorted to the top' : 'Off — sorted by age, like everything else'}
          </span>
        </span>
      </Field>

      <Field
        last
        label="What the patient is told"
        desc={
          <>
            <strong className="text-[var(--text-secondary)]">Nothing, and this cannot be changed.</strong> An automatic{' '}
            <em>&ldquo;someone will be in touch&rdquo;</em> is a promise about a timescale nobody has agreed, and a
            patient who does not hear back by the time they expected is angrier than one who was never promised
            anything.
          </>
        }
      >
        <span className="inline-flex items-center gap-1.5 text-[12.5px] font-semibold text-[var(--text-muted)]">
          <Icon kind="lock" size={12} stroke={2} />
          No handoff message is sent
        </span>
      </Field>
    </Shell>
  );
}

/* ────────────────────────────── shared shell ────────────────────────────── */

function Shell({
  open,
  onClose,
  title,
  sub,
  footerNote,
  valid,
  onSubmit,
  children,
}: {
  open: boolean;
  onClose: () => void;
  title: string;
  sub: React.ReactNode;
  footerNote: React.ReactNode;
  valid: boolean;
  onSubmit: () => void;
  children: React.ReactNode;
}) {
  return (
    <ViewportOverlay open={open} onClose={onClose}>
      <div
        onClick={(e) => { e.stopPropagation(); }}
        className="flex max-h-[90vh] w-[min(620px,calc(100vw-32px))] flex-col overflow-hidden rounded-[18px] border border-[var(--border-default)] bg-[var(--surface-card)] shadow-[0_18px_48px_rgba(36,41,54,.18)]"
      >
        <div className="flex items-start justify-between gap-3 border-b border-[var(--border-default)] px-6 py-5">
          <div>
            <h2 className="m-0 text-[17px] font-bold tracking-[-0.02em] text-[var(--text-primary)]">{title}</h2>
            <p className="m-0 mt-1 max-w-[460px] text-[11.5px] leading-[1.6] text-[var(--text-muted)]">{sub}</p>
          </div>
          <button
            type="button"
            onClick={onClose}
            aria-label="Close"
            className="flex h-8 w-8 shrink-0 items-center justify-center rounded-[10px] border border-[var(--border-default)] text-[var(--text-muted)] hover:bg-[var(--surface-sunken)]"
          >
            <Icon kind="x" size={14} stroke={2} />
          </button>
        </div>

        <div className="min-h-0 flex-1 overflow-y-auto">{children}</div>

        <div className="flex items-center justify-between gap-4 border-t border-[var(--border-default)] bg-[var(--surface-sunken)] px-6 py-4">
          <span className="text-[11.5px] leading-[1.6] text-[var(--text-muted)]">{footerNote}</span>
          <span className="flex shrink-0 items-center gap-2">
            <button
              type="button"
              onClick={onClose}
              className="rounded-[10px] border border-[var(--border-default)] bg-[var(--surface-card)] px-4 py-2 text-[13.5px] font-semibold text-[var(--text-secondary)] hover:bg-[var(--surface-header)]"
            >
              Cancel
            </button>
            <button
              type="button"
              disabled={!valid}
              onClick={onSubmit}
              className={cn(
                'rounded-[10px] bg-[var(--brand-indigo)] px-4 py-2 text-[13.5px] font-semibold text-white',
                !valid && 'cursor-not-allowed opacity-40',
              )}
            >
              Add rule
            </button>
          </span>
        </div>
      </div>
    </ViewportOverlay>
  );
}

function Field({
  label,
  desc,
  children,
  last,
}: {
  label: string;
  desc?: React.ReactNode;
  children: React.ReactNode;
  last?: boolean;
}) {
  return (
    <div className="px-6 py-4" style={last ? undefined : { borderBottom: '1px solid #edf0f6' }}>
      <div className="text-[13px] font-bold text-[var(--text-primary)]">{label}</div>
      {desc ? <p className="m-0 mt-1 text-[11.5px] leading-[1.6] text-[var(--text-muted)]">{desc}</p> : null}
      <div className="mt-2.5">{children}</div>
    </div>
  );
}

/** A real radio group — three options is a radio group, not three buttons (§3.9). */
function RadioGroup<T extends string>({
  name,
  value,
  options,
  onChange,
}: {
  name: string;
  value: T;
  options: { value: T; label: string; desc: React.ReactNode }[];
  onChange: (v: T) => void;
}) {
  return (
    <div role="radiogroup" className="flex flex-col gap-2">
      {options.map((o) => {
        const active = o.value === value;
        return (
          <label
            key={o.value}
            className={cn(
              'flex cursor-pointer items-start gap-3 rounded-[10px] border px-3.5 py-3',
              active
                ? 'border-[var(--brand-indigo)] bg-[var(--brand-indigo-tint)]'
                : 'border-[var(--border-default)] hover:bg-[var(--surface-sunken)]',
            )}
          >
            <input
              type="radio"
              name={name}
              checked={active}
              onChange={() => { onChange(o.value); }}
              className="mt-0.5 accent-[var(--brand-indigo)]"
            />
            <span className="min-w-0">
              <span className="block text-[13px] font-semibold text-[var(--text-primary)]">{o.label}</span>
              <span className="mt-0.5 block text-[11.5px] leading-[1.6] text-[var(--text-muted)]">{o.desc}</span>
            </span>
          </label>
        );
      })}
    </div>
  );
}
